import { Marker } from '@m1/ol-map';
import useMountEffect from '@restart/hooks/useMountEffect';
import classNames from 'classnames/bind';
import React, { useState, useMemo } from 'react';
import {
  RiCompasses2Line,
  RiDvdLine,
  RiGitPullRequestLine,
  RiTimerLine,
  RiRestartLine,
  RiSpeedUpLine,
  RiArrowUpDownLine,
  RiPinDistanceLine,
  RiVipDiamondLine,
  RiRemoteControlLine,
  RiToolsLine,
  RiListSettingsLine,
  RiToggleLine,
  RiFocus3Line,
  RiExchangeLine,
} from 'react-icons/ri';
import { useDispatch, useSelector, useStore } from 'react-redux';

import CondDistance from './CondDistance';
import CondYaw from './CondYaw';
import CusSurvey from './CusSurvey';
import DoAutotuneEnable from './DoAutotuneEnable';
import DoChangeSpeed from './DoChangeSpeed';
import DoGripper from './DoGripper';
import DoGuidedLimits from './DoGuidedLimits';
import DoJump from './DoJump';
import DoParachute from './DoParachute';
import DoRepeatRelay from './DoRepeatRelay';
import DoRepeatServo from './DoRepeatServo';
import DoSetRelay from './DoSetRelay';
import DoSetRoiLocation from './DoSetRoiLocation';
import DoSetRoiNone from './DoSetRoiNone';
import DoSetServo from './DoSetServo';
import DoVtolTransition from './DoVtolTransition';
import styles from './index.module.scss';
import NavContinueAndChangeAlt from './NavContinueAndChangeAlt';
import NavDelay from './NavDelay';
import NavGuidedEnable from './NavGuidedEnable';
import NavLand from './NavLand';
import NavLoiterTime from './NavLoiterTime';
import NavLoiterToAlt from './NavLoiterToAlt';
import NavLoiterTurns from './NavLoiterTurns';
import NavReturnToLaunch from './NavReturnToLaunch';
import NavTakeoff from './NavTakeoff';
import NavWaypoint from './NavWaypoint';

import actions from '@/actions';
import AccordionCaret from '@/components/ui/AccordionCaret';
import Select from '@/components/ui/kit/Select';
import MoreMenu from '@/components/ui/MoreMenu';
import EventEmitter from '@/libs/EventEmitter';
import { NotifierService as notifier } from '@/libs/Notifier';

const cx = classNames.bind(styles);

const Command = ({ index, data: command }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const markers = useSelector((state) => state.editor.markers);
  const [isOpenBody, setIsOpenBody] = useState(true);

  // ROI, Land, Loiter 3종 (Time, Altitude, Turns), Waypoint 경우 유형 변경 가능
  const isChangeableType = useMemo(() => {
    return ['doSetRoiLocation', 'navLand', 'navLoiterTime', 'navLoiterToAlt', 'navLoiterTurns', 'navWaypoint'].includes(
      command.type,
    );
  }, [command.type]);

  const markerLabel = useMemo(() => {
    const { id, type } = command;

    if (markers[id]) {
      return markers[id].label;
    } else if (type === 'cusSurvey') {
      return markers[`${id}/survey/start`]?.label ?? null;
    }

    return null;
  }, [markers]);

  const Icon = useMemo(() => {
    // prettier-ignore
    switch (command.type) {
      case 'condDistance':            return RiPinDistanceLine;
      case 'condYaw':                 return RiRestartLine;
      case 'doAutotuneEnable':        return RiToolsLine;
      case 'doChangeSpeed':           return RiSpeedUpLine;
      case 'doGripper':               return RiCompasses2Line;
      case 'doGuidedLimits':          return RiListSettingsLine;
      case 'doJump':                  return RiGitPullRequestLine;
      case 'doParachute':             return RiVipDiamondLine;
      case 'doRepeatRelay':
      case 'doSetRelay':              return RiToggleLine;
      case 'doSetRoiNone':            return RiFocus3Line;
      case 'doRepeatServo':
      case 'doSetServo':              return RiDvdLine;
      case 'doVtolTransition':        return RiExchangeLine;
      case 'navContinueAndChangeAlt': return RiArrowUpDownLine;
      case 'navDelay':                return RiTimerLine;
      case 'navGuidedEnable':         return RiRemoteControlLine;

      // Use default marker icon below for these types.
      case 'doSetRoiLocation':
      case 'navLand':
      case 'navLoiterTime':
      case 'navLoiterToAlt':
      case 'navLoiterTurns':
      case 'navReturnToLaunch':
      case 'navTakeoff':
      case 'navWaypoint':
      case 'cusSurvey':
      default:
        return;
    }
  }, [command.type]);

  const specs = useMemo(() => {
    return store.getState().mission.json.specs;
  }, []);

  useMountEffect(() => {
    const toggle = (nextIsOpenBody) => {
      if (nextIsOpenBody === undefined) {
        setIsOpenBody((prev) => !prev);
      } else {
        setIsOpenBody(nextIsOpenBody);
      }
    };

    const subscribeToken = EventEmitter.subscribe(`command/${command.id}/toggle`, toggle);

    return () => {
      EventEmitter.unsubscribe(subscribeToken);
    };
  });

  const changeType = (e) => {
    dispatch(actions.mission.mavlink.changeCommandType(index, e.target.value));
  };

  const toggleBody = () => {
    setIsOpenBody(!isOpenBody);
  };

  const remove = () => {
    if (store.getState().map3d.isShow) {
      notifier.error('Commands cannot be deleted in 3D map.');
      return;
    }

    dispatch(actions.mission.removeCommand(index));
  };

  const moveUp = () => {
    if (index < 2) {
      notifier.error('Cannot be moved up anymore.');
      return;
    }

    dispatch(actions.mission.shiftCommand(index, -1));
  };

  const moveDown = () => {
    if (index === store.getState().mission.json.commands.length - 1) {
      notifier.error('Cannot be moved down anymore.');
      return;
    }

    dispatch(actions.mission.shiftCommand(index, 1));
  };

  const prepareToMoveTo = () => {
    // Takeoff 외 미션이 하나만 있는 경우
    if (store.getState().mission.json.commands.length === 2) {
      notifier.error('No available positions to move.');
      return;
    }

    dispatch(actions.editor.setMoveFromIndex(index));
    dispatch(actions.editor.changeMode(null));
  };

  return (
    <div className={cx('container')}>
      <div className={cx('header')} onClick={toggleBody}>
        <div className={cx('left')}>
          {markerLabel && (
            <div className={cx('marker')}>
              <Marker label={markerLabel} />
            </div>
          )}
          {Icon && (
            <div className={cx('icon')}>
              <Icon size={20} color="white" />
            </div>
          )}
          {
            // prettier-ignore
            <Select
              value={command.type}
              disabled={!isChangeableType || store.getState().editor.moveFromIndex !== null}
              className={cx('type')}
              onChange={changeType}>
                <option value="doAutotuneEnable" hidden>Autotune Enable</option>
                <option value="doSetRoiNone" hidden>Cancel ROI</option>
                <option value="navContinueAndChangeAlt" hidden>Change Altitude</option>
                <option value="doChangeSpeed" hidden>Change Speed</option>
                <option value="doRepeatRelay" hidden>Cycle Relay</option>
                <option value="doRepeatServo" hidden>Cycle Servo</option>
                <option value="navDelay" hidden>Delay Until</option>
                <option value="doGripper" hidden>Gripper</option>
                <option value="navGuidedEnable" hidden>Guided Enable</option>
                <option value="doGuidedLimits" hidden>Guided Limits</option>
                <option value="doJump" hidden>Jump To Item</option>
                {specs.frame !== 'rover' && <option value="navLand">Land</option>}
                {specs.frame !== 'rover' && <option value="navLoiterToAlt">Loiter (Altitude)</option>}
                <option value="navLoiterTime">Loiter (Time)</option>
                {specs.firmware === 'ardupilot' && <option value="navLoiterTurns">Loiter (Turns)</option>}
                <option value="doSetRoiLocation">Region Of Interest</option>
                <option value="navReturnToLaunch" hidden>Return To Launch</option>
                <option value="doSetRelay" hidden>Set Relay</option>
                <option value="doSetServo" hidden>Set Servo</option>
                <option value="doVtolTransition" hidden>VTOL Transition</option>
                <option value="cusSurvey" hidden>Survey</option>
                <option value="navTakeoff" hidden>Takeoff</option>
                <option value="doParachute" hidden>Trigger Parachute</option>
                <option value="condDistance" hidden>Wait For Distance</option>
                <option value="condYaw" hidden>Wait For Yaw</option>
                <option value="navWaypoint">Waypoint</option>
            </Select>
          }
        </div>
        <div className={cx('right')}>
          {command.type !== 'navTakeoff' && (
            <MoreMenu
              data={[
                { name: 'Remove', callback: remove },
                { name: 'Move Up', callback: moveUp },
                { name: 'Move Down', callback: moveDown },
                { name: 'Move To', callback: prepareToMoveTo },
              ]}
            />
          )}
          <AccordionCaret up={isOpenBody} size={32} />
        </div>
      </div>
      <div className={cx(['body', { open: isOpenBody }])}>
        {
          // prettier-ignore
          <div className={cx('inner')}>
            {command.type === 'condDistance' && <CondDistance index={index} data={command} />}
            {command.type === 'condYaw' && <CondYaw index={index} data={command} />}
            {command.type === 'doAutotuneEnable' && <DoAutotuneEnable index={index} data={command} />}
            {command.type === 'doChangeSpeed' && <DoChangeSpeed index={index} data={command} />}
            {command.type === 'doGripper' && <DoGripper index={index} data={command} />}
            {command.type === 'doGuidedLimits' && <DoGuidedLimits index={index} data={command} />}
            {command.type === 'doJump' && <DoJump index={index} data={command} />}
            {command.type === 'doParachute' && <DoParachute index={index} data={command} />}
            {command.type === 'doRepeatRelay' && <DoRepeatRelay index={index} data={command} />}
            {command.type === 'doRepeatServo' && <DoRepeatServo index={index} data={command} />}
            {command.type === 'doSetRelay' && <DoSetRelay index={index} data={command} />}
            {command.type === 'doSetRoiLocation' && <DoSetRoiLocation index={index} data={command} />}
            {command.type === 'doSetRoiNone' && <DoSetRoiNone index={index} data={command} />}
            {command.type === 'doSetServo' && <DoSetServo index={index} data={command} />}
            {command.type === 'doVtolTransition' && <DoVtolTransition index={index} data={command} />}
            {command.type === 'navContinueAndChangeAlt' && (<NavContinueAndChangeAlt index={index} data={command} />)}
            {command.type === 'navDelay' && <NavDelay index={index} data={command} />}
            {command.type === 'navGuidedEnable' && <NavGuidedEnable index={index} data={command} />}
            {command.type === 'navLand' && <NavLand index={index} data={command} />}
            {command.type === 'navLoiterTime' && <NavLoiterTime index={index} data={command} />}
            {command.type === 'navLoiterToAlt' && <NavLoiterToAlt index={index} data={command} />}
            {command.type === 'navLoiterTurns' && <NavLoiterTurns index={index} data={command} />}
            {command.type === 'navReturnToLaunch' && <NavReturnToLaunch />}
            {command.type === 'navTakeoff' && <NavTakeoff index={index} data={command} />}
            {command.type === 'navWaypoint' && <NavWaypoint index={index} data={command} />}
            {command.type === 'cusSurvey' && <CusSurvey index={index} data={command} />}
          </div>
        }
      </div>
    </div>
  );
};

export default Command;
